/**
 * Booking and trip shapes, ported from the user app's `types/deliveries.ts`.
 *
 * The app is the authority: these are the same wire shapes its
 * `deliveryService` consumes, narrowed to what a web customer journey needs.
 * Where the app's types and the backend disagree, the comments record what the
 * backend actually sends — that disagreement is real and already cost the app a
 * normalisation layer (see `safeArray` in its home screen).
 */

export type TripExperiencePhase =
  | 'DRIVER_APPROACHING_PICKUP'
  | 'DRIVER_ON_ROUTE_TO_PICKUP'
  | 'DRIVER_AT_PICKUP'
  | 'DRIVER_HEADING_TO_DROPOFF'
  | 'AWAITING_COMPLETION'
  | 'UNKNOWN';

export type TripFlow = 'INSTANT' | 'ASSIGNMENT';

interface TripSummaryBase {
  bookingId: string;
  tripId: string;
  driverId: string | null;
  status: string;
  phase: TripExperiencePhase;
  vehicleType: string;
  pickupAddress?: string | null;
  dropoffAddress?: string | null;
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
  dropoffLatitude?: number | null;
  dropoffLongitude?: number | null;
  stale: boolean;
  staleReason?: string | null;
}

export interface ActiveInstantTripSummary extends TripSummaryBase {
  flow: TripFlow;
  etaSeconds?: number | null;
  blockingNewInstantTrips: boolean;
}

export interface TripTrackingBreadcrumbPoint {
  latitude?: number | null;
  longitude?: number | null;
  at?: string | null;
}

export interface TripTrackingSnapshot {
  tripId?: string | null;
  driverId?: string | null;
  breadcrumb?: TripTrackingBreadcrumbPoint[] | null;
  phase?: string | null;
  etaToPickupSec?: number | null;
  etaToDropoffSec?: number | null;
  routePolyline?: string | null;
  routeRevision?: number | null;
  routeDeviation?: boolean | null;
  routeAvailable?: boolean | null;
  routeStatus?: string | null;
  locationTrusted?: boolean | null;
  trackingMode?: string | null;
  expectedUpdateIntervalSec?: number | null;
  lastSeenAt?: string | null;
  updatedAt?: string | null;
}

export interface TripTrackingLatestLocation {
  tripId?: string | null;
  driverId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  at?: string | null;
  sequenceNumber?: number | null;
  phase?: string | null;
  etaToPickupSec?: number | null;
  etaToDropoffSec?: number | null;
  routePolyline?: string | null;
  routeRevision?: number | null;
  routeDeviation?: boolean | null;
  routeAvailable?: boolean | null;
  routeStatus?: string | null;
  locationTrusted?: boolean | null;
  trackingMode?: string | null;
  expectedUpdateIntervalSec?: number | null;
  lastSeenAt?: string | null;
  updatedAt?: string | null;
}

export interface UpcomingAssignmentTripSummary extends TripSummaryBase {
  flow: 'ASSIGNMENT';
  /** ISO date-time. */
  scheduledAt: string;
}

/**
 * Normalised summary.
 *
 * The app's own type declares `activeInstantTrip` as a single object, but the
 * backend returns a list and the app coerces it at the call site. This type
 * declares the honest shape — always arrays — and `getActiveTripSummary`
 * performs the coercion once so no caller has to remember.
 */
export interface ActiveTripSummary {
  activeTrips: ActiveInstantTripSummary[];
  upcomingTrips: UpcomingAssignmentTripSummary[];
}

export type BookingStatus =
  | 'ASSIGNED'
  | 'SEARCHING'
  | 'NO_DRIVER_FOUND'
  | 'DRAFT'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'ON_ROUTE_TO_PICKUP'
  | 'EXPIRED'
  | 'LOADING'
  | 'ENROUTE'
  | 'UNLOADING'
  | 'DELIVERED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'SCHEDULED'
  | 'PENDING'
  | 'CANCELLED_BY_SYSTEM'
  | 'PICKED_UP';

/** Statuses that mean the job is over, whatever the outcome. */
export const FINISHED_STATUSES: BookingStatus[] = [
  'COMPLETED',
  'CANCELLED',
  'CANCELLED_BY_SYSTEM',
  'EXPIRED',
  'NO_DRIVER_FOUND',
];

/**
 * A past booking. Narrowed hard from the app's `BookingSummary`, which carries
 * payment mirror fields, driver lists and fare breakdowns that a read-only
 * history list has no use for. Fields are optional where the app's own type
 * marks them so.
 */
export interface BookingSummary {
  id: string;
  tripId: string;
  code: string;
  createdAt: string;
  scheduledAt?: string;
  status: BookingStatus;
  pickupShort: string;
  dropoffShort: string;
  distanceKm?: number | null;
  vehicleType: string;
  fareAmount?: number | null;
  estimatedFare?: number | null;
  currency?: string;
}

/**
 * A file uploaded against a trip. Ported from the app's `FileMetadataDTO`,
 * narrowed to what a read-only list needs to name, date and open one.
 *
 * Everything but nothing is optional because the backend fills these from two
 * different places: rows created by the presign flow carry full metadata, while
 * older trips only left a bare URL behind in `attachmentUrls`.
 */
export interface TripAttachment {
  id?: string | null;
  url?: string | null;
  originalName?: string | null;
  contentType?: string | null;
  uploadedAt?: string | null;
  createdAt?: string | null;
}

/** One driver's standing on a trip, as the app's offer-status screen lists it. */
export interface TripDetailDriver {
  driverId: string;
  name?: string | null;
  rating?: number | null;
  avatarUrl?: string | null;
  status: BookingStatus;
  lastEventAt?: string | null;
}

/**
 * The full record of one trip — the same payload the app's "Offer Status"
 * screen renders, narrowed to the read-only half.
 *
 * Dropped on purpose relative to the app's `OfferStatus`: the per-driver event
 * log, the offer counters (`viewed`, `delivered`, `expired`…) and the payout
 * mirror fields. Those exist to drive actions the web does not offer —
 * rescheduling, unlinking a driver, chasing an offer — and listing a count
 * nobody can act on is noise.
 */
export interface TripDetail {
  tripId: string;
  code: string;
  userId?: string | null;
  createdAt: string;
  scheduledAt?: string | null;
  status: BookingStatus;
  pickupShort: string;
  dropoffShort: string;
  loadNotes?: string | null;
  /**
   * Encoded Google polyline for the route that was driven.
   *
   * BOTH SPELLINGS ARE REAL. The backend sends `pollyLine` on some versions of
   * this endpoint and `polyline` on others; the app reads `polyline ?? pollyLine`
   * for exactly this reason. Reading only one silently produces a trip with no
   * route, which looks like missing data rather than a missed field.
   */
  polyline?: string | null;
  pollyLine?: string | null;
  distanceKm?: number | null;
  durationMin?: number | null;
  vehicleType: string;
  fare?: {
    currency?: string | null;
    estimated?: number | null;
    finalAmount?: number | null;
    method?: string | null;
    last4?: string | null;
  } | null;
  /** Payment mirror fields — the subset the app's banner reads. */
  paymentState?: string | null;
  paymentFailureState?: string | null;
  drivers?: TripDetailDriver[] | null;
  attachments?: TripAttachment[] | null;
  attachmentUrls?: string[] | null;
}

/** Note the lower-case `i` — this is the backend's spelling, not a typo. */
export interface BookingHistoryRequest {
  userid: string;
  statuses?: BookingStatus[];
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface RouteSummaryRequest {
  pickup: LatLng;
  dropoff: LatLng;
}

export interface RouteSummaryResponse {
  distanceMeters: number;
  durationSeconds?: number | null;
  /** Encoded Google polyline. Decode with `utils/polyline`. */
  polyline?: string | null;
}

/**
 * Instant trip request. Snake_case is the wire contract, not a style choice —
 * the backend reads these exact keys.
 */
export interface TripRequestPayload {
  user_lat: number;
  user_lng: number;
  dest_lat: number;
  dest_lng: number;
  vehicle_type: string;
  estimated_fare: number;
  /** The saved card's public id, from user-management. */
  payment_method: string;
  notes_for_driver?: string | null;
}

export interface TripSearchResponse {
  tripId: string;
  status: BookingStatus;
  dispatchState?: 'SEARCHING' | 'PENDING' | 'ACCEPTED' | 'NO_DRIVER_FOUND';
  paymentState?: string | null;
  message?: string | null;
  /** ISO timestamp after which the search stops. */
  expiresAt?: string;
}
